import React, { useEffect, useState } from "react";
import { Box, Button, Link, Typography, useTheme } from "@mui/material";
import { useTranslation } from "react-i18next";
import WealthAPILogo from "@images/wealthapi-logo.svg?url";
import WealthAPIWhiteLogo from "@images/wealthapi-logo-white.svg?url";
import { ScrollableWithGradients } from "@rentablo/components";
import { Modal } from "@rentablo/components";
import DiagonalLineIcon from "@icons/myicons/diagonal-line.svg?react";
import { useLogout, usePrivacyConsent } from "@hooks";
import { useToken } from "@rentablo/api";
import KpmgLogo from "@icons/myicons/sidebar/kpmg.svg?react";

const KPMG_PRIVACY_PDF_URL =
  "https://assets.kpmg.com/content/dam/kpmgsites/de/pdf/misc/datenschutzinformationen-fuer-betroffene-kpmg-ag-wpg.pdf";

export const KpmgPrivacyConsentModal = () => {
  const theme = useTheme();
  const { t } = useTranslation();
  const { logout } = useLogout();
  const { data: token } = useToken();
  const { hasConsented, acceptConsent } = usePrivacyConsent();
  const [hasScrolledToEnd, setHasScrolledToEnd] = useState(false);
  const isOpen = !hasConsented;

  useEffect(() => {
    if (isOpen) {
      // eslint-disable-next-line react-hooks/set-state-in-effect -- reset on modal reopen
      setHasScrolledToEnd(false);
    }
  }, [isOpen, token]);

  if (!token) {
    return null;
  }

  const handleAgree = () => {
    acceptConsent();
  };

  const handleDisagree = () => {
    logout();
  };

  return (
    <Modal
      isOpen={isOpen}
      borderRadius="12px"
      title={
        <Box
          display="flex"
          justifyContent="center"
          alignItems="center"
          gap={4}
          mb={4}
        >
          <Box
            component="img"
            src={
              theme.palette.mode === "dark" ? WealthAPIWhiteLogo : WealthAPILogo
            }
            alt="wealth API"
            sx={{ height: 48 }}
          />
          <DiagonalLineIcon />
          <KpmgLogo />
        </Box>
      }
      width={800}
      maxHeight="80vh"
      disableScrollLock
      testId="kpmg-privacy-modal"
    >
      <ScrollableWithGradients
        onScrollStateChange={(data) =>
          setHasScrolledToEnd(data.isScrolledToBottom)
        }
        recalculateDeps={[isOpen]}
        sx={{ maxHeight: "50vh", px: 7.5, mb: 4 }}
        data-testid="kpmg-privacy-scroll-container"
      >
        <Typography variant="h5" gutterBottom>
          {t("privacyConsent.title")}
        </Typography>

        <Typography variant="body3" paragraph>
          {t("privacyConsent.intro")}
        </Typography>

        <Typography variant="h6" gutterBottom>
          {t("privacyConsent.purposes.title")}
        </Typography>
        <Typography variant="body3" component="div" sx={{ mb: 2 }}>
          {t("privacyConsent.purposes.intro")}
          <ul>
            <li>{t("privacyConsent.purposes.item1")}</li>
            <li>{t("privacyConsent.purposes.item2")}</li>
            <li>{t("privacyConsent.purposes.item3")}</li>
          </ul>
        </Typography>

        <Typography variant="h6" gutterBottom>
          {t("privacyConsent.categories.title")}
        </Typography>
        <Typography variant="body3" paragraph>
          {t("privacyConsent.categories.categoriesIntro")}
        </Typography>

        <Typography variant="subtitle1" gutterBottom>
          {t("privacyConsent.categories.identity.title")}
        </Typography>
        <Typography variant="body3" component="div" sx={{ mb: 2 }}>
          <ul>
            <li>{t("privacyConsent.categories.identity.item1")}</li>
            <li>{t("privacyConsent.categories.identity.item2")}</li>
            <li>{t("privacyConsent.categories.identity.item3")}</li>
          </ul>
        </Typography>

        <Typography variant="subtitle1" gutterBottom>
          {t("privacyConsent.categories.financial.title")}
        </Typography>
        <Typography variant="body3" component="div" sx={{ mb: 2 }}>
          <ul>
            <li>{t("privacyConsent.categories.financial.item1")}</li>
            <li>{t("privacyConsent.categories.financial.item2")}</li>
            <li>{t("privacyConsent.categories.financial.item3")}</li>
          </ul>
        </Typography>

        <Typography variant="subtitle1" gutterBottom>
          {t("privacyConsent.categories.investments.title")}
        </Typography>
        <Typography variant="body3" component="div" sx={{ mb: 2 }}>
          <ul>
            <li>{t("privacyConsent.categories.investments.item1")}</li>
            <li>{t("privacyConsent.categories.investments.item2")}</li>
            <li>{t("privacyConsent.categories.investments.item3")}</li>
            <li>{t("privacyConsent.categories.investments.item4")}</li>
          </ul>
        </Typography>

        <Typography variant="h6" gutterBottom>
          {t("privacyConsent.relatives.title")}
        </Typography>
        <Typography variant="body3" paragraph>
          {t("privacyConsent.relatives.text")}
        </Typography>

        <Typography variant="h6" gutterBottom>
          {t("privacyConsent.moreInfo.title")}
        </Typography>
        <Typography variant="body3" paragraph>
          {t("privacyConsent.moreInfo.text")}
        </Typography>
        <Link
          href={KPMG_PRIVACY_PDF_URL}
          target="_blank"
          rel="noopener noreferrer"
          pb={10}
        >
          {t("privacyConsent.moreInfo.linkLabel")}
        </Link>
      </ScrollableWithGradients>

      <Box
        display="flex"
        gap="20px"
        sx={{ px: "38px", pb: 4 }}
        data-testid="kpmg-privacy-actions"
      >
        <Button
          variant="tertiary"
          onClick={handleDisagree}
          data-testid="kpmg-privacy-disagree"
          sx={{ flex: 1 }}
        >
          {t("privacyConsent.buttons.disagree")}
        </Button>
        <Button
          variant="contained"
          color="primary"
          disabled={!hasScrolledToEnd}
          onClick={handleAgree}
          data-testid="kpmg-privacy-agree"
          sx={{ flex: 1 }}
        >
          {t("privacyConsent.buttons.agree")}
        </Button>
      </Box>
    </Modal>
  );
};
